import { X, Chrome, Mail } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

/**
 * Match Assessment — signup gate.
 *
 * Shows Google (Emergent OAuth) and email/password side-by-side. On success,
 * calls the /api/match/unlock endpoint to reveal the full ranked results and
 * hands control back to the parent via onUnlocked().
 */
export default function MatchGate({ open, sessionId, onClose, onUnlocked }) {
    const { refresh } = useAuth();
    const [mode, setMode] = useState('choose'); // 'choose' | 'email'
    const [isLogin, setIsLogin] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    if (!open) return null;

    const backend = process.env.REACT_APP_BACKEND_URL;
    const redirectUrl = `${window.location.origin}/match/unlock?sid=${encodeURIComponent(sessionId)}`;
    const googleUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;

    const submitEmail = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
            const endpoint = isLogin ? '/auth/login' : '/auth/register';
            const payload = isLogin ? { email, password } : { email, password, name };
            await api.post(endpoint, payload);
            await refresh();
            const { data } = await api.post('/match/unlock', { session_id: sessionId });
            onUnlocked(data.results);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail[0]?.msg : (err.message || 'Something went wrong')));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center" data-testid="match-gate">
            {/* backdrop */}
            <div className="absolute inset-0 bg-black/70" onClick={onClose} />
            {/* panel */}
            <div className="relative w-full max-w-lg bg-white border border-neutral-200 mx-6">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-neutral-500 hover:text-black"
                    data-testid="match-gate-close"
                    aria-label="Close"
                >
                    <X className="w-5 h-5" />
                </button>
                <div className="p-8 lg:p-10">
                    <div className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data">
                        Unlock your full report
                    </div>
                    <h3 className="font-editorial mt-4 leading-tight" style={{ fontSize: '30px', letterSpacing: '-0.01em' }}>
                        See all 10 matches, ranked and explained.
                    </h3>
                    <p className="mt-3 text-[14px] text-neutral-600 leading-relaxed">
                        Create a free account to reveal the other nine neighbourhoods, save your assessment,
                        and get one honest Sunday briefing when a match sees material change.
                    </p>

                    {mode === 'choose' && (
                        <div className="mt-8 space-y-3">
                            <a
                                href={googleUrl}
                                data-testid="match-gate-google"
                                className="flex items-center justify-center gap-3 w-full border border-brand-navy bg-brand-navy text-white px-5 py-3.5 text-[14px] font-medium hover:bg-brand-gold hover:text-brand-navy transition-colors"
                               
                            >
                                <Chrome className="w-4 h-4" /> Continue with Google
                            </a>
                            <button
                                onClick={() => setMode('email')}
                                data-testid="match-gate-email-toggle"
                                className="flex items-center justify-center gap-3 w-full border border-neutral-400 text-black px-5 py-3.5 text-[14px] font-medium hover:bg-brand-navy hover:text-white hover:border-brand-navy transition-colors"
                               
                            >
                                <Mail className="w-4 h-4" /> Continue with email
                            </button>
                            <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-400 font-mono-data pt-3 text-center">
                                No listings · No spam · One click to unsubscribe
                            </div>
                        </div>
                    )}

                    {mode === 'email' && (
                        <form onSubmit={submitEmail} className="mt-8 space-y-3" data-testid="match-gate-email-form">
                            {!isLogin && (
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="First name"
                                    className="w-full border border-neutral-300 px-4 py-3 text-[14px] focus:outline-none focus:border-black"
                                   
                                    data-testid="match-gate-name"
                                />
                            )}
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Email address"
                                required
                                className="w-full border border-neutral-300 px-4 py-3 text-[14px] focus:outline-none focus:border-black"
                               
                                data-testid="match-gate-email"
                            />
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder={isLogin ? 'Password' : 'Create a password (8+ characters)'}
                                required
                                minLength={isLogin ? 1 : 8}
                                className="w-full border border-neutral-300 px-4 py-3 text-[14px] focus:outline-none focus:border-black"
                               
                                data-testid="match-gate-password"
                            />
                            {error && (
                                <div className="text-[12px] text-red-700 border border-red-300 bg-red-50 px-3 py-2">
                                    {error}
                                </div>
                            )}
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="match-gate-email-submit"
                                className="w-full bg-brand-navy text-white px-5 py-3.5 text-[14px] font-medium hover:bg-neutral-800 disabled:opacity-50"
                               
                            >
                                {busy ? 'Working…' : (isLogin ? 'Sign in and unlock' : 'Create account and unlock')}
                            </button>
                            <div className="flex items-center justify-between pt-2 text-[12.5px] text-neutral-600">
                                <button
                                    type="button"
                                    onClick={() => { setIsLogin(!isLogin); setError(''); }}
                                    className="link-editorial"
                                    data-testid="match-gate-toggle-login"
                                >
                                    {isLogin ? 'Need an account? Register' : 'Already have an account? Sign in'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setMode('choose'); setError(''); }}
                                    className="link-editorial"
                                >
                                    ← Back
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
